import { Injectable, PLATFORM_ID, Inject, inject } from '@angular/core';
import {
    Bar,
    ChartTypeFavorites,
    ChartingLibraryFeatureset,
    DatafeedConfiguration,
    ErrorCallback,
    Favorites,
    HistoryCallback,
    LibrarySymbolInfo,
    Overrides,
    PeriodParams,
    ResolutionString,
    ResolveCallback,
    SearchSymbolResultItem,
    SearchSymbolsCallback,
    SubscribeBarsCallback,
    SymbolResolveExtension,
    WidgetOverrides
} from 'src/assets/library/charting_library/charting_library';
import { CoinPair } from '../models/coin.model';
import { environment } from 'src/environments/environment';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { isPlatformBrowser } from '@angular/common';
import { Observable, map, tap } from 'rxjs';
import { APP_CONSTANTS } from '../constants/enum.constants';
import { ApiService } from './api.service';

type KLineHttpData = [number, string, string, string, string, string, number, string, number, string, string, string];
type BinanceKLineSocketData = {
    e: 'kline'; // Event type
    E: number; // Event time
    s: string; // Symbol
    k: {
        t: number; // Kline start time
        T: number; // Kline close time
        s: string; // Symbol
        i: string; // Interval
        f: number; // First trade ID
        L: number; // Last trade ID
        o: string; // Open price
        c: string; // Close price
        h: string; // High price
        l: string; // Low price
        v: string; // Base asset volume
        n: number; // Number of trades
        x: boolean; // Is this kline closed?
        q: string; // Quote asset volume
        V: string; // Taker buy base asset volume
        Q: string; // Taker buy quote asset volume
        B: string; // Ignore
    };
};
interface BinanceKLineStream {
    stream: `${string}@kline_${string}`;
    data: BinanceKLineSocketData;
}

@Injectable({
    providedIn: 'root'
})
export class TradingViewService {
    /**
     * Variables used to establish and handle Trading View web socket connection
     */
    private static payloadId: number = 100;
    private tradingViewSocketSubject!: WebSocketSubject<any>;
    tradingViewSocketObs!: Observable<any>;
    private _listeners: Map<BinanceKLineStream['stream'], SubscribeBarsCallback> = new Map();

    private coinPairs: CoinPair[] = [];
    private readonly defaultCoinPairList: string[] = ['USDT', 'BUSD', 'BTC', 'ETH', 'BNB', 'DAI'];

    private apiService = inject(ApiService);

    constructor(@Inject(PLATFORM_ID) private platformId: Object) {
        if (isPlatformBrowser(this.platformId)) {
            this.tradingViewSocketSubject = webSocket(`${environment.BINANCE_SPOT_WS_URL}stream`);
            this.tradingViewSocketObs = this.tradingViewSocketSubject.asObservable();
        }
    }

    /**
     * Variables used to control the UI and options in Trading View Chart
     * _resolutions ~ An array of supported resolutions
     *
     * _binanceIntervalMap ~ JSON with key as the supported resolution and value as it's equivalent interval according
     * to the binance kline socket documentation
     *
     * _datafeedConfiguration ~ Initial configuration of the datafeed of the Trading View Chart
     *
     * _darkModeOverrides ~ Trading View chart custom theme configuration for dark mode
     *
     * _lightModeOverrides ~ Trading View chart custom theme configuration for light mode
     *
     * _enabledFeatures ~ Features enabled in the Trading View chart
     *
     * _disabledFeatures ~ Features disabled in the Trading View chart
     *
     * _defaultFavorites ~ Configurations marked as favorites by default
     */
    private readonly _resolutions: ResolutionString[] = [
        '1',
        '3',
        '15',
        '30',
        '60',
        '120',
        '240',
        '360',
        '480',
        '720',
        '1D',
        '3D',
        '1W',
        '3W',
        '1M'
    ] as ResolutionString[];

    private readonly _binanceIntervalMap: { [key: string]: string } = {
        '1': '1m',
        '3': '3m',
        '5': '5m',
        '15': '15m',
        '30': '30m',
        '60': '1h',
        '120': '2h',
        '240': '4h',
        '360': '6h',
        '480': '8h',
        '720': '12h',
        '1D': '1d',
        '3D': '3d',
        '1W': '1w',
        '3W': '3w',
        '1M': '1M'
    };

    private readonly _datafeedConfiguration: DatafeedConfiguration = {
        supports_time: true,
        supports_marks: false,
        supports_timescale_marks: false,
        symbols_types: [{ name: 'crypto', value: 'crypto' }],
        supported_resolutions: this._resolutions
    };

    private readonly _darkModeOverrides: Partial<WidgetOverrides> = {
        'candleStyle.upColor': '#73a822',
        'candleStyle.downColor': '#E4080B',
        'paneProperties.background': '#1a1a1a',
        'paneProperties.vertGridProperties.color': 'rgba(42, 46, 57, 0.06)',
        'paneProperties.horzGridProperties.color': 'rgba(42, 46, 57, 0.06)'
    };

    private readonly _lightModeOverrides: Partial<WidgetOverrides> = {
        'candleStyle.upColor': '#73a822',
        'candleStyle.downColor': '#E4080B',
        'paneProperties.background': '#ffffff',
        'paneProperties.vertGridProperties.color': 'rgba(42, 46, 57, 0.06)',
        'paneProperties.horzGridProperties.color': 'rgba(42, 46, 57, 0.06)'
    };

    private readonly _enabledFeatures: ChartingLibraryFeatureset[] = [
        'header_chart_type',
        'hide_left_toolbar_by_default',
        'use_localstorage_for_settings'
    ];

    private readonly _disabledFeatures: ChartingLibraryFeatureset[] = [
        'header_symbol_search',
        'header_compare',
        'timeframes_toolbar'
    ];

    private readonly _defaultFavorites: Favorites<ChartTypeFavorites> = {
        intervals: ['15', '60', '240', '1D', '1W', '1M'] as ResolutionString[],
        chartTypes: ['Area', 'Line', 'Candles']
    };

    private lastBarsCache: Map<string, Bar> = new Map();

    /**
     * GET method to retrieve private readonly properties of the trading view chart
     * @returns DataFeed Configuration
     */
    getDatafeedConfig(): DatafeedConfiguration {
        return this._datafeedConfiguration;
    }

    /**
     * GET method to retrieve private readonly properties of the trading view chart
     * @returns Array of supported resolutions
     */
    getResolutions(): ResolutionString[] {
        return this._resolutions;
    }

    /**
     * GET method to retrieve private readonly properties of the trading view chart
     * @param key An element of the array of supported resolutions
     * @returns Interval used in the binance socket to denote the supported resolution
     */
    getBinanceInterval(key: string): string {
        return this._binanceIntervalMap[key];
    }

    /**
     * GET method to retrieve private readonly properties of the trading view chart
     * @param isDarkMode true if the current theme is dark mode, false otherwise
     * @returns The Partial<Overrides> used to handle the theme of the trading view chart
     */
    getThemeOverrides(isDarkMode: boolean): Partial<Overrides> {
        if (isDarkMode) return this._darkModeOverrides;
        return this._lightModeOverrides;
    }

    /**
     * GET method to retrieve private readonly properties of the trading view chart
     * @param key enabled | disabled
     * @returns Set of features for the trading view chart
     */
    getFeatures(key: 'enabled' | 'disabled'): ChartingLibraryFeatureset[] {
        if (key === 'enabled') return this._enabledFeatures;
        return this._disabledFeatures;
    }

    getFavorites(): Favorites<ChartTypeFavorites> {
        return this._defaultFavorites;
    }

    /**
     * Http call to fetch KLine data for the specified pair name for a particular interval
     * duration and a certain limit
     */
    async fetchKLines(
        pairName: string,
        interval: string,
        startTime: string,
        endTime: string,
        limit: string
    ): Promise<KLineHttpData[]> {
        const url: URL = new URL(`${environment.BINANCE_API_URL}v3/uiKlines`);
        url.searchParams.append('symbol', pairName);
        url.searchParams.append('interval', interval);
        url.searchParams.append('startTime', startTime);
        url.searchParams.append('endTime', endTime);
        url.searchParams.append('limit', limit);

        return await fetch(url)
            .then(res => res.json())
            .then((response: KLineHttpData[]) => response)
            .catch(_ => []);
    }

    /**
     * HTTP call to fetch the pairs for the coin code specified in the payload
     * @param CoinCode coinCode
     * @returns Coin Pairs Observable
     */
    fetchCoinPairs(coinCode: string): Observable<CoinPair[]> {
        const payload = {
            coin_codes: [coinCode],
            pair_type: APP_CONSTANTS.DEFAULT_PAIR_TYPE
        };
        return this.apiService.fetch<{ coinPairs: CoinPair[] }>('COIN_PAIRS', payload, coinCode).pipe(
            map(response => response.coinPairs),
            tap(response => (this.coinPairs = response))
        );
    }

    findMatchingPair(coinSymbol): CoinPair | undefined {
        let matchedCoinPair: CoinPair | undefined;

        // Try to find a match where the coin symbol is the base and the default symbol is the quote
        this.defaultCoinPairList.some(defaultPair => {
            matchedCoinPair = this.coinPairs.find(
                coinObj => coinObj.base_coin_code === coinSymbol && coinObj.quote_coin_code === defaultPair
            );
            return !!matchedCoinPair; // Stop iterating once a match is found
        });

        // If no match found in the first loop, try to find a match where the coin symbol is the quote and the default symbol is the base
        if (!matchedCoinPair) {
            this.defaultCoinPairList.some(defaultPair => {
                matchedCoinPair = this.coinPairs.find(
                    coinObj => coinObj.quote_coin_code === coinSymbol && coinObj.base_coin_code === defaultPair
                );
                return !!matchedCoinPair; // Stop iterating once a match is found
            });
        }

        return matchedCoinPair; // Return the found match or undefined if no match was found
    }

    searchSymbols(
        userInput: string,
        exchange: string,
        symbolType: string,
        onResultReadyCallback: SearchSymbolsCallback
    ): void {
        const SearchSymbolResultItems: SearchSymbolResultItem[] = this.coinPairs
            .filter(pair => pair.pair_name.indexOf(userInput.toUpperCase()) > 0)
            .map(
                ele =>
                    ({
                        symbol: ele.pair_name,
                        full_name: ele.pair_name,
                        description: ele.base_coin_code + '/' + ele.quote_coin_code,
                        ticker: ele.pair_name,
                        exchange: exchange,
                        type: symbolType
                    } as SearchSymbolResultItem)
            );
        onResultReadyCallback(SearchSymbolResultItems);
    }

    resolveSymbol(
        symbolName: string,
        onSymbolResolvedCallback: ResolveCallback,
        onResolveErrorCallback: ErrorCallback,
        extension?: SymbolResolveExtension
    ): void {
        const searchResult = this.coinPairs.find(pair => pair.pair_name === symbolName.toUpperCase());
        if (!searchResult) {
            onResolveErrorCallback(`cannot resolve symbol`);
            return;
        }
        const librarySymbolInfo = {
            minmov: 1,
            type: 'crypto',
            format: 'price',
            session: '24x7',
            has_daily: true,
            exchange: 'ixfi',
            has_intraday: true, // this is false in official doc
            volume_precision: 2,
            has_no_volume: true,
            timezone: 'Etc/UTC',
            listed_exchange: 'ixfi',
            data_status: 'streaming',
            visible_plots_set: 'ohlcv',
            has_weekly_and_monthly: true, // this is false in official doc,
            name: searchResult.pair_name,
            ticker: searchResult.pair_name,
            full_name: `${searchResult.base_coin_code}${searchResult.quote_coin_code}`,
            currency_code: searchResult.quote_coin_code,
            supported_resolutions: this._resolutions,
            pricescale: Math.round(1 / searchResult.tick_size), // this is 100 in the official doc,
            description: searchResult.base_coin_code + '/' + searchResult.quote_coin_code
        } as LibrarySymbolInfo;
        onSymbolResolvedCallback(librarySymbolInfo);
    }

    async getBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString,
        periodParams: PeriodParams,
        onHistoryCallback: HistoryCallback,
        onErrorCallback: ErrorCallback
    ) {
        const { from, to, firstDataRequest } = periodParams;
        const fromTimeStamp: string = (from * 1000).toString();
        const toTimeStamp: string = (to * 1000).toString();
        const limit: string = '1000';
        const interval = this._binanceIntervalMap[resolution];
        try {
            const kLineData: KLineHttpData[] = await this.fetchKLines(
                symbolInfo.name,
                interval,
                fromTimeStamp,
                toTimeStamp,
                limit
            );
            if (!kLineData) {
                onHistoryCallback([], { noData: true });
                return;
            }
            let bars: Bar[] = [];
            kLineData.forEach(bar => {
                if (bar[0] >= Number(fromTimeStamp) && bar[0] < Number(toTimeStamp)) {
                    bars = [
                        ...bars,
                        {
                            time: bar[0],
                            open: parseFloat(bar[1]),
                            high: parseFloat(bar[2]),
                            low: parseFloat(bar[3]),
                            close: parseFloat(bar[4]),
                            volume: parseFloat(bar[5])
                        }
                    ];
                }
            });
            if (firstDataRequest) this.lastBarsCache.set(symbolInfo.name, { ...bars[bars.length - 1] });
            onHistoryCallback(bars, { noData: false });
        } catch (err) {
            onErrorCallback('Error in [getBars]');
        }
    }

    subscribeBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString,
        onRealtimeCallback: SubscribeBarsCallback,
        subscriberUID: string,
        onResetCacheNeededCallback: () => void
    ): void {
        const binanceInterval: string = this._binanceIntervalMap[resolution];
        const symbolName: string = symbolInfo.name.toLowerCase();
        const kLineStream: BinanceKLineStream['stream'] = `${symbolName}@kline_${binanceInterval}`;
        this.tradingViewSocketSubject.next({
            method: 'SUBSCRIBE',
            params: [kLineStream],
            id: (TradingViewService.payloadId += 1)
        });
        this._listeners.set(kLineStream, onRealtimeCallback);
    }

    unsubscribeBars(subscriberUID: string): void {
        const UIDInfo: string[] = subscriberUID.split('_#_');
        const symbolName: string = UIDInfo[0].toLowerCase();
        const binanceInterval: string = this._binanceIntervalMap[UIDInfo[2]];
        const kLineStream: BinanceKLineStream['stream'] = `${symbolName}@kline_${binanceInterval}`;
        this.tradingViewSocketSubject.next({
            method: 'UNSUBSCRIBE',
            params: [kLineStream],
            id: 1
        });
        if (this._listeners.has(kLineStream)) this._listeners.delete(kLineStream);
    }

    filterSocketResponse(response: BinanceKLineStream): boolean {
        return !response.hasOwnProperty('result') && this._listeners.has(response.stream);
    }

    processSocketResponse(response: BinanceKLineStream) {
        const kLineSocketResponse = response.data.k;
        const newBar: Bar = {
            time: kLineSocketResponse.t,
            open: parseFloat(kLineSocketResponse.o),
            high: parseFloat(kLineSocketResponse.h),
            low: parseFloat(kLineSocketResponse.l),
            close: parseFloat(kLineSocketResponse.l),
            volume: parseFloat(kLineSocketResponse.v)
        };
        this._listeners.get(response.stream)!(newBar);
    }
}
